//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { facebookInstagramSettingsActions } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsActions';
import {
  setOnboarded,
  getClientIsOnboarded as fetchClientIsOnboarded,
  getPageMetadata as fetchPageMetadata,
} from '@apis/gatewayFacebookApi';
import { FACEBOOK_PAGE_ACTIONS } from '@facebook/constants/facebookConstants';
import { IFacebookPageMetadata } from '@facebook/models/facebookPostDetailsModel';
import FacebookPageId from '@models/FacebookPageId';
import { createAction } from '@utils/actions';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { FetchActionCreators, makeFetchActions } from '@utils/makeFetchActions';

import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';

function requestPageMetadata() {
  return createAction({
    type: FACEBOOK_PAGE_ACTIONS.REQUEST_PAGE_METADATA,
  });
}

function receivePageMetadata(pageMetadata: IFacebookPageMetadata) {
  return createAction({
    type: FACEBOOK_PAGE_ACTIONS.RECEIVE_PAGE_METADATA,
    pageMetadata,
  });
}

const getFacebookAndInstagramConnectionInfo = makeFetchActions(
  FACEBOOK_PAGE_ACTIONS.REQUEST_FB_IG_CONNECTION_INFO,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FB_IG_CONNECTION_INFO_SUCCEEDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FB_IG_CONNECTION_INFO_FAILED,
);

const getFacebookPageConnectionInfo = makeFetchActions(
  FACEBOOK_PAGE_ACTIONS.REQUEST_FACEBOOK_PAGE_CONNECTION_INFO,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FACEBOOK_PAGE_CONNECTION_INFO_SUCCEEDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FACEBOOK_PAGE_CONNECTION_INFO_FAILED,
  (isClientOnboarded: boolean) => ({ isClientOnboarded }),
);

const setIsClientOnboarded = makeFetchActions(
  FACEBOOK_PAGE_ACTIONS.REQUEST_SET_IS_ONBOARDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_SET_IS_ONBOARDED_SUCCEEDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_SET_IS_ONBOARDED_FAILED,
);

export type FacebookPageActions = ReturnType<
  | typeof requestPageMetadata
  | typeof receivePageMetadata
  | FetchActionCreators<typeof getFacebookAndInstagramConnectionInfo>
  | FetchActionCreators<typeof getFacebookPageConnectionInfo>
  | FetchActionCreators<typeof setIsClientOnboarded>
>;

const getPageMetadata =
  (pageId: FacebookPageId) => async (dispatch: ReduxDispatch) => {
    const response = await fetchPageMetadata(pageId);

    dispatch(receivePageMetadata(response));
  };

export const facebookPageActions = {
  getPageMetadata,
  fetchPageTokenAndMetadata:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const { connectedFacebookPageToken } =
        getState().facebookInstagramSettings;

      if (isUndefined(connectedFacebookPageToken)) {
        await dispatch(
          facebookInstagramSettingsActions.fetchConnectedFacebookPage(),
        );
      }

      const facebookPageId =
        getState().facebookInstagramSettings.connectedFacebookPageToken
          ?.entityId;

      if (isDefined(facebookPageId)) {
        await dispatch(getPageMetadata(facebookPageId));
      }
    },
  fetchFacebookAndInstagramConnectionInfo:
    () => async (dispatch: ReduxDispatch) => {
      dispatch(getFacebookAndInstagramConnectionInfo.requested());

      try {
        await Promise.all([
          dispatch(
            facebookInstagramSettingsActions.fetchConnectedFacebookPage(),
          ),
          dispatch(facebookInstagramSettingsActions.fetchFacebookUserToken()),
          dispatch(
            facebookInstagramSettingsActions.fetchConnectedInstagramAccount(),
          ),
        ]);
        dispatch(getFacebookAndInstagramConnectionInfo.succeeded());
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(getFacebookAndInstagramConnectionInfo.failed(err));
      }
    },
  fetchFacebookPageConnectionInfo: () => async (dispatch: ReduxDispatch) => {
    dispatch(getFacebookPageConnectionInfo.requested());
    try {
      const [isClientOnboarded] = await Promise.all([
        fetchClientIsOnboarded(),
        dispatch(facebookPageActions.fetchFacebookAndInstagramConnectionInfo()),
      ]);
      dispatch(getFacebookPageConnectionInfo.succeeded(isClientOnboarded));
    } catch (e) {
      const err = getRequestErrorMessage(e) ?? '';
      dispatch(getFacebookPageConnectionInfo.failed(err));
    }
  },
  setIsClientOnboarded: () => async (dispatch: ReduxDispatch) => {
    dispatch(setIsClientOnboarded.requested());
    try {
      await setOnboarded();
      dispatch(setIsClientOnboarded.succeeded());
    } catch (e) {
      const err = getRequestErrorMessage(e) ?? '';
      dispatch(setIsClientOnboarded.failed(err));
    }
  },
};
